// CRM Edit/New modal — full form with all person fields, photos, follow-ups
// (legacy inline list) and linked videos/studios for existing records.
import React, { useEffect, useState } from "react";
import { X, Users } from "lucide-react";
import { api } from "../../lib/api";
import { TYPES, SOURCES, STATUS_LABEL, TYPE_LABELS, statusesForType, typeLabel, getRecordStatuses, toggleRecordStatus, primaryStatus } from "./constants";
import { configFor, getValue, putValue } from "./typeFields";
import PhotoUploader from "./PhotoUploader";
import FollowUpsList from "./FollowUpsList";
import ShootsList from "./ShootsList";
import { LinkedVideos, LinkedStudios } from "./Linked";

// Shared neon input class
const neonInputClass = "w-full px-4 py-3 rounded-xl text-white placeholder-zinc-600 transition-all focus:outline-none";
const neonInputStyle = {
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(255,16,240,0.2)"
};
const neonInputFocusStyle = { borderColor: "#FF10F0", boxShadow: "0 0 20px rgba(255,16,240,0.3)" };

function F({ l, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider mb-2 text-purple-300">{l}</div>
      {children}
    </div>
  );
}

function Sel({ v, set, options = [], testid, labelMap, placeholder, required = false }) {
  // Keep a stored value selectable even when it is not in the current option list.
  const opts = v && !options.includes(v) ? [v, ...options] : options;
  return (
    <select value={v || ""} onChange={(e) => set(e.target.value)} data-testid={testid} required={required}
      className="w-full px-4 py-3 rounded-xl text-white transition-all appearance-none cursor-pointer"
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,16,240,0.2)"
      }}>
      {placeholder && <option value="" className="bg-zinc-900">{placeholder}</option>}
      {opts.map((o) => <option key={o} value={o} className="bg-zinc-900">{(labelMap && labelMap[o]) || o}</option>)}
    </select>
  );
}

function Chips({ v, set, options, testid }) {
  const selected = Array.isArray(v) ? v : [];
  const all = [...selected.filter((s) => !options.includes(s)), ...options];
  const toggle = (o) => set(selected.includes(o) ? selected.filter((s) => s !== o) : [...selected, o]);
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid} role="group">
      {all.map((o) => {
        const on = selected.includes(o);
        return (
          <button key={o} type="button" onClick={() => toggle(o)} aria-pressed={on}
            className="px-3 py-1.5 rounded-full text-xs font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-400"
            style={{
              background: on ? "rgba(255,16,240,0.18)" : "rgba(255,255,255,0.03)",
              border: `1px solid ${on ? "#FF10F0" : "rgba(255,16,240,0.2)"}`,
              color: on ? "#ffd6fb" : "#d4d4d8",
            }}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

function StatusChips({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label="Record statuses" data-testid="crm-f-status">
      {statusesForType().map((status) => {
        const selected = value.includes(status);
        return (
          <button key={status} type="button" aria-pressed={selected} data-testid={`crm-f-status-${status}`}
            onClick={() => onChange(toggleRecordStatus(value, status))}
            className="px-3 py-1.5 rounded-full text-xs font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-300"
            style={{
              background: selected ? "rgba(255,16,240,0.2)" : "rgba(255,255,255,0.03)",
              border: `1px solid ${selected ? "#FF10F0" : "rgba(255,16,240,0.2)"}`,
              color: selected ? "#ffd6fb" : "#d4d4d8",
            }}>
            {STATUS_LABEL[status] || status.replace(/_/g, " ")}
          </button>
        );
      })}
    </div>
  );
}

export default function CRMForm({ editing, form, setForm, onClose, onSave }) {
  const cfg = configFor(form.type);
  const [catalogVideos, setCatalogVideos] = useState([]);
  const [catalogState, setCatalogState] = useState("loading");
  const setField = (key, value) => setForm(putValue(form, key, value));
  const selectedStatuses = getRecordStatuses(form);
  const setStatuses = (statuses) => setForm((previous) => ({
    ...previous,
    statuses,
    status: primaryStatus(statuses),
  }));

  useEffect(() => {
    if (!cfg.lists.some((list) => list.key === "shoots")) {
      setCatalogVideos([]);
      setCatalogState("idle");
      return undefined;
    }
    let active = true;
    setCatalogState("loading");
    api.get("/api/catalog")
      .then(({ data }) => {
        if (active) {
          setCatalogVideos(Array.isArray(data) ? data : Array.isArray(data?.videos) ? data.videos : []);
          setCatalogState("loaded");
        }
      })
      .catch((error) => {
        if (active) {
          setCatalogVideos([]);
          setCatalogState(error?.response?.status === 401 || error?.response?.status === 403 ? "denied" : "error");
        }
      });
    return () => { active = false; };
  }, [cfg]);

  // Auto-update legal consent status based on OnlyFans/JFF entries
  const handleOnlyFansChange = (value) => {
    const updates = { onlyfans: value };
    if (value && value.trim()) {
      updates.legal_consent_status = "OnlyFans Verified Member";
    } else if (!form.justforfans || !form.justforfans.trim()) {
      updates.legal_consent_status = "";
    }
    setForm({ ...form, ...updates });
  };
  
  const handleJFFChange = (value) => {
    const updates = { justforfans: value };
    if (value && value.trim()) {
      updates.legal_consent_status = "JFF Verified Member";
    } else if (!form.onlyfans || !form.onlyfans.trim()) {
      updates.legal_consent_status = "";
    }
    setForm({ ...form, ...updates });
  };

  const customChange = { onlyfans: handleOnlyFansChange, justforfans: handleJFFChange };
  const renderField = (f) => {
    const value = getValue(form, f.key);
    const onChange = customChange[f.key] || ((val) => setField(f.key, val));
    let control;
    if (f.kind === "select") {
      control = <Sel v={value} set={onChange} options={f.options} testid={f.testid} placeholder="Select..." required={f.required} />;
    } else if (f.kind === "chips") {
      control = <Chips v={value} set={onChange} options={f.options} testid={f.testid} />;
    } else if (f.kind === "textarea") {
      control = <textarea data-testid={f.testid} value={value || ""} rows={3} required={f.required} onChange={(e) => onChange(e.target.value)} className={neonInputClass} style={neonInputStyle} />;
    } else {
      control = <input data-testid={f.testid} type={f.kind === "text" ? "text" : f.kind} value={value ?? ""} placeholder={f.placeholder} required={f.required} onChange={(e) => onChange(e.target.value)} className={neonInputClass} style={neonInputStyle} />;
    }
    return <div key={f.key} className={f.wide ? "sm:col-span-2" : undefined}><F l={f.required ? `${f.label} *` : f.label}>{control}</F></div>;
  };
  
  return (
    <div className="fixed inset-0 backdrop-blur-xl z-50 flex items-center justify-center p-3 sm:p-4" style={{ background: "rgba(0,0,0,0.92)" }} onClick={onClose} data-testid="crm-modal">
      <div className="rounded-3xl border max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-2xl" style={{ background: "linear-gradient(135deg, rgba(20,20,30,0.98) 0%, rgba(10,10,20,0.98) 100%)", borderColor: "rgba(255,16,240,0.3)", boxShadow: "0 0 80px rgba(255,16,240,0.25), 0 20px 60px rgba(0,0,0,0.8)" }} onClick={(e) => e.stopPropagation()}>
        <div className="p-4 sm:p-6 border-b flex items-center justify-between gap-4" style={{ borderColor: "rgba(255,16,240,0.2)", background: "linear-gradient(90deg, rgba(168,85,247,0.08) 0%, rgba(255,16,240,0.08) 100%)" }}>
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: "#FF10F0" }}>{editing === "new" ? "NEW RECORD" : "EDIT RECORD"} · {typeLabel(form.type)}</div>
            <h2 className="font-heading text-xl sm:text-2xl text-white truncate">{form.stage_name || cfg.nameLabel}</h2>
          </div>
          <button onClick={onClose} aria-label="Close record form" data-testid="crm-modal-close" className="p-2 rounded-lg text-zinc-500 hover:text-pink-400 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-400">
            <X size={20} />
          </button>
        </div>
        <div className="p-4 sm:p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <F l={`${cfg.nameLabel} *`}>
              <input data-testid="crm-f-stage" value={form.stage_name || ""} onChange={(e) => setForm({ ...form, stage_name: e.target.value })} required aria-required="true" className={neonInputClass} style={neonInputStyle} />
            </F>
            {cfg.legalLabel && <F l={cfg.legalLabel}><input data-testid="crm-f-legal" value={form.legal_name || ""} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} className={neonInputClass} style={neonInputStyle} /></F>}
            <div>
              <F l="Type"><Sel v={form.type} set={(v) => setForm({ ...form, type: v })} options={TYPES} labelMap={TYPE_LABELS} testid="crm-f-type" /></F>
              <div className="mt-1.5 text-[11px]" style={{ color: "var(--gio-muted-2)" }}>The form adapts to the selected record type.</div>
            </div>
            <div className="sm:col-span-2"><F l="Status"><StatusChips value={selectedStatuses} onChange={setStatuses} /></F></div>
            <F l="Source"><Sel v={form.source} set={(v) => setForm({ ...form, source: v })} options={SOURCES} testid="crm-f-source" /></F>
            {cfg.fields.map(renderField)}
          </div>

          {cfg.consent && form.legal_consent_status && (
            <div className="rounded-lg border p-3 flex items-center gap-2" style={{ borderColor: "rgba(34,197,94,0.4)", background: "rgba(34,197,94,0.08)" }}>
              <span className="text-2xl">✓</span><div><div className="text-xs font-bold uppercase tracking-wider text-green-400">Legal Consent Status</div><div className="text-sm text-green-300">{form.legal_consent_status}</div></div>
            </div>
          )}

          <F l="Notes"><textarea data-testid="crm-f-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className={neonInputClass} style={neonInputStyle} /></F>
          <PhotoUploader photos={form.photos || []} onChange={(photos) => setForm({ ...form, photos })} max={30} hint={cfg.photoHint} />

          {cfg.lists.map((list) => (
            <ShootsList key={list.key} shoots={getValue(form, list.key) || []} onChange={(value) => setField(list.key, value)} kindLabel={list.label} catalogVideos={catalogVideos} catalogState={catalogState} />
          ))}

          {editing !== "new" && (
            <>
              <FollowUpsList items={form.follow_ups || []} onChange={(follow_ups) => setForm((f) => ({ ...f, follow_ups }))} title={cfg.followUpsTitle || "FOLLOW-UPS"} />
              <LinkedVideos crmId={editing} />
              <LinkedStudios crmId={editing} />
            </>
          )}
        </div>
        <div className="p-4 sm:p-6 border-t flex justify-end gap-3" style={{ borderColor: "rgba(255,16,240,0.2)" }}>
          <button onClick={onClose} data-testid="crm-cancel" className="px-5 py-3 rounded-xl font-bold transition-all hover:bg-white/5 text-zinc-400 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-400">Cancel</button>
          <button onClick={onSave} data-testid="crm-save" className="px-5 py-3 rounded-xl font-bold inline-flex items-center gap-2 transition-all text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" style={{ background: "linear-gradient(135deg, #FF10F0 0%, #a855f7 100%)", boxShadow: "0 0 30px rgba(255,16,240,0.4)" }}>
            <Users size={16} /> {editing === "new" ? "Add record" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
